import type { V2CreateAdminProjectInput } from '@idc-forms/schemas/api-v2/admin-projects'
import {
  CircleOff,
  CirclePlay,
  FileText,
  FolderOpen,
  Images,
  Info,
  Pencil,
  Trash2
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { DocumentPortfolioTab } from '@/features/documents/components/document-portfolio-tab'
import { GalleryTabWithCopy } from '@/features/documents/components/gallery-tab-with-copy'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteProjectV2Mutation,
  useUpdateProjectV2Mutation
} from '../hooks/use-project-v2-mutations'
import type { ProjectV2AdminRow } from '../models/project-admin-v2'
import { ProjectLogo } from './project-badge'
import { ProjectV2Form } from './project-v2-form'
import { ProjectActiveBadge, ProjectCurrencyBadge } from './projects-admin-columns'

type ProjectV2DetailPanelProps = {
  project: ProjectV2AdminRow
}

/**
 * Panel de detalle del proyecto contra la API v2. Espeja el panel v1 —mismo
 * `ProjectV2Form`, mismo logo, mismos badges, mismos acordeones de reservas y
 * actividad— y sólo cambia el transporte: direcciona por `prj_`, la sociedad
 * llega como `{ id: soc_, name }`, y la autoría y los autorizadores ya resueltos
 * a `usr_`.
 */
export function ProjectV2DetailPanel({ project }: ProjectV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const navigate = useNavigate()
  const updateMutation = useUpdateProjectV2Mutation()
  const deleteMutation = useDeleteProjectV2Mutation()
  const { has } = usePermissions()

  const toggleActive = () => {
    updateMutation.mutate({ id: project.id, body: { active: !project.active } })
  }

  if (mode === 'edit') {
    return (
      <ProjectV2Form
        defaultValues={{
          name: project.name,
          code: project.code,
          // El SDK tipa los ids como string plano; el form usa los branded del
          // schema (`soc_`/`usr_`). Mismo dato en runtime.
          society: project.society?.id as V2CreateAdminProjectInput['society'] | undefined,
          currency: project.currency,
          image: project.image ?? undefined,
          // ☠️ Se PRECARGAN los dos: sin esto el campo abre vacío y guardar cualquier OTRO cambio
          // lo borra, porque el contrato reduce la cadena vacía a `null` —«borralo»—. El logo de
          // la app entra al mismo molde el día que se cargó, así que hereda la misma trampa.
          mark: project.mark ?? undefined,
          address: project.address ?? undefined,
          requiresAuthorization: project.requiresAuthorization,
          authorizerIds: project.authorizers.map(
            (authorizer) => authorizer.id
          ) as V2CreateAdminProjectInput['authorizerIds'],
          active: project.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: project.id, body: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ProjectLogo
              name={project.name}
              mark={project.mark}
              code={project.code}
              className="h-8 w-8"
            />
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold leading-tight">{project.name}</h3>
              {project.society && (
                <p className="truncate text-sm text-muted-foreground">{project.society.name}</p>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<ProjectActiveBadge active={project.active} />} />
            <DetailFieldRow label="Código" value={project.code} copyValue={project.code} />
            <DetailFieldRow label="Sociedad" value={project.society?.name ?? '—'} />
            <DetailFieldRow
              label="Moneda"
              value={<ProjectCurrencyBadge currency={project.currency} />}
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateProjects)
              ? [
                  {
                    label: 'Editar proyecto',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    // El editor de plantillas es una PÁGINA completa (`:id/docs`), no un panel.
                    // Navega con el `prj_` porque es el único id que la fila v2 conoce; la API
                    // v1 lo resuelve igual que un `_id` (ver `publicIdFilter`).
                    label: 'Plantillas de documentos',
                    icon: <FileText className="h-4 w-4" />,
                    variant: 'outline' as const,
                    onClick: () => {
                      closePanel()
                      void navigate(PATHS.admin.business.projects.documents(project.id))
                    }
                  },
                  {
                    label: project.active ? 'Desactivar proyecto' : 'Activar proyecto',
                    icon: project.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteProjects)
              ? [
                  {
                    label: 'Eliminar proyecto',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        {/*
          `ScrollFade` alrededor de la `TabsList`, como ya lo tiene el subtipo. No es adorno:
          estas pestañas viven en un panel que se arrastra hasta 320 px, y ahí tres pestañas no
          entran. Sin esto la última se RECORTA sin ninguna señal de que hay más — el usuario ve
          dos pestañas y concluye que son dos. El degradado del borde dice que sigue, y el
          `w-max min-w-full` deja que la lista crezca en vez de comprimir los rótulos.
        */}
        <ScrollFade className="w-full" scrollClassName="overflow-y-hidden">
          <TabsList className="flex h-auto w-max min-w-full justify-normal rounded-none">
            <TabsTrigger className="flex gap-1" value="more-info">
              <Info className="size-4" /> Más información
            </TabsTrigger>
            {/* «Cartapacio», no «Documentos»: el proyecto YA tiene una pantalla llamada
              así —el editor de plantillas de contrato— y dos cosas homónimas en la
              misma entidad es una trampa esperando. */}
            <TabsTrigger className="flex gap-1" value="portfolio">
              <FolderOpen className="size-4" /> Cartapacio
            </TabsTrigger>

            {/* La galería va aparte del cartapacio porque el trabajo es otro: ahí se
                clasifica una lista de papeles, acá se decide qué se ve y en qué orden. */}
            <TabsTrigger className="flex gap-1" value="gallery">
              <Images className="size-4" /> Galería
            </TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['reservations', 'activity']}>
            <AccordionItem value="reservations">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Reservas
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Requiere autorización"
                    value={project.requiresAuthorization ? 'Sí' : 'No'}
                  />
                  {project.requiresAuthorization && (
                    <DetailFieldRow
                      label="Autorizadores"
                      value={
                        project.authorizers.length > 0 ? (
                          <span className="flex flex-wrap gap-1">
                            {project.authorizers.map((authorizer) => (
                              <UserChip key={authorizer.id} name={authorizer.fullname} />
                            ))}
                          </span>
                        ) : (
                          '—'
                        )
                      }
                    />
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(project.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={project.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(project.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={project.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
        <TabsContent value="portfolio">
          <DocumentPortfolioTab ownerType="project" ownerId={project.id} />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTabWithCopy ownerType="project" ownerId={project.id} ownerLabel={project.code} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar proyecto</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{project.name}”. Los inmuebles y negociaciones históricas que lo
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(project.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
